import React, { useState, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { Link } from 'react-router-dom';
import { omit } from 'lodash';
import { collection, doc, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { useToggle } from 'react-use';

import { functions } from '../firebase';
import createComputeSalesJob, { startRakutenSyncAndBackfill, BACKFILL_DAYS } from '../createComputeSalesJob';
import { fields as shopFields, shopifyCredentialFieldNames, yahooCredentialFieldNames, canUseShopify as canUseShopifyFor } from '../shared/models/shop';
import useUser from './hooks/useUser';
import env from '../env';
import ModelFormModal from './modals/ModelFormModal';
import RakutenFeeNote from './RakutenFeeNote';

const testRakutenApiConnection = httpsCallable(functions, 'testRakutenApiConnection');

const processShopValues = (values) => ({
  // Shopify の連携情報は OAuth コールバックが書くため、hidden フィールドの null で
  // 上書きしてしまわないよう除去する
  ...omit(values, [...shopifyCredentialFieldNames, ...yahooCredentialFieldNames]),
  ...(values.rakutenShopUrl && {
    rakutenShopUrl: values.rakutenShopUrl.replace(/^https?:\/\/.*rakuten\.co\.jp\//, '').replace(/\//g, ''),
  }),
});

const amazonGuide = {
  sections: [
    {
      heading: 'Amazonセラーの連携手順',
      steps: [
        'Amazonのログイン画面が表示されるので、セラーセントラルのアカウントでログインしてください。',
        '下記のようなアクセス権限の確認画面が表示されます。チェックボックスにチェックを入れて「サインアップ/Totonoにログイン」をクリックします。',
        '自動的にTotonoに戻り、セラーが一覧に追加されれば完了です。',
      ],
      image: '/images/guide/amazon-sp-api-confirm.png',
      imageCaption: 'チェックを入れて「サインアップ/Totonoにログイン」をクリック',
      note: 'セラーセントラルにログイン済みの場合はログイン画面がスキップされます。連携完了後、売上データの取り込みが自動で始まります。反映まで少し時間がかかることがあります。',
    },
  ],
};

const rakutenGuide = {
  sections: [
    {
      heading: 'serviceSecret・ライセンスキーの確認方法',
      steps: [
        'RMSにログインします。',
        '「店舗向け 情報・サービス」を開きます。',
        '「6 WEB APIサービス」>「2-1 アプリ一覧」を開きます。',
        'アプリ登録者名「店舗様」の「詳細」をクリックします。',
        '「確認する」をクリックすると「最新の利用情報」が表示されます。',
      ],
      image: '/images/guide/rakuten-rms-keys.webp',
      imageCaption: '①がserviceSecret、②がライセンスキーです',
      note: '「店舗様」が表示されない場合は「2-2 アプリ登録」から申込を行ってください。ライセンスキーは約3ヶ月ごとに更新が必要です。',
      referenceUrl: 'https://navi-manual.faq.rakuten.net/service/000010308',
    },
    {
      heading: 'ショップURLの確認方法',
      steps: [
        'RMSにログインします。',
        '右上の店舗名をクリックすると、店舗URLが確認できます。',
      ],
    },
  ],
};

const adApiStep = {
  key: 'adApi',
  label: '広告APIを設定する',
  description: 'Amazon広告データを取り込んで、広告費を含めた損益を正確に把握しましょう。',
  path: '/settings/adApiAccounts',
  skippable: true,
  skipLabel: '広告を使用していないのでスキップ',
  guide: {
    title: '広告API設定の手順',
    sections: [
      {
        heading: 'Amazon広告APIの認証',
        steps: [
          '広告APIアカウントページの「認証する」ボタンをクリックします。',
          'Amazonの広告アカウントでログインし、アクセスを許可します。',
          '自動的にTotonoに戻るので、紐づけるアカウントを選択して「完了」をクリックします。',
        ],
      },
      {
        heading: 'セラーとの連携',
        steps: [
          '認証が完了したら、一覧の「セラーと連携する」ボタンをクリックします。',
          '広告プロファイルが自動的に取得され、対応するセラーと紐づきます。',
          'ショップ連携ページで、セラーに「広告: アカウント名」と表示されれば連携完了です。',
        ],
        note: '広告データは1日1回・早朝に取り込まれるため、認証直後は数値が表示されません。認証の翌朝以降に反映されます。',
      },
    ],
  },
};

const shopIntegrationStep = {
  key: 'shopIntegration',
  label: 'ショップを連携する',
  description: 'Amazonセラーや楽天の店舗を連携して、売上データを自動で取り込みましょう。',
};

// 商品同期はショップ連携時に自動で走るようになったため、ガイドの手動「同期」ステップは廃止。
// 取り込みの進行状況はヘッダーの「過去データを取り込み中」バーで示す。
function buildSteps({ sellers, shops }) {
  const hasAmazon = sellers.length > 0;
  const hasAny = hasAmazon || shops.length > 0;

  // Amazon がある(or 未連携でこれから追加しうる)場合は広告ステップも出す。楽天のみなら不要。
  if (!hasAny || hasAmazon) return [shopIntegrationStep, adApiStep];
  return [shopIntegrationStep];
}

export default function SetupGuide({ sellers, shops, adApiAccounts, tenant, isLoading }) {
  const { user } = useUser();
  // Shopify は審査・検証中のため、運営者と審査担当者用アカウントにのみ表示する
  const canUseShopify = canUseShopifyFor(user);
  const [dismissed, setDismissed] = useState(false);
  const [guideModal, setGuideModal] = useState(null);
  const [skippedSteps, setSkippedSteps] = useState({});
  const [shopModal, setShopModal] = useState(null); // null | 'select' | 'amazon' | 'rakuten'

  const steps = useMemo(
    () => buildSteps({ sellers, shops }),
    [sellers.length, shops.length],
  );

  const completionStatus = {
    shopIntegration: sellers.length > 0 || shops.length > 0,
    adApi: skippedSteps.adApi || adApiAccounts.some(_ => _.adApi?.refreshToken != null),
  };

  const completedCount = steps.filter(_ => completionStatus[_.key]).length;
  const allCompleted = completedCount === steps.length;

  if (isLoading || dismissed || allCompleted) return null;

  const currentStep = steps.find(_ => !completionStatus[_.key]);

  return (
    <>
      <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f4f8 100%)' }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h6 className="fw-bold mb-1">セットアップガイド</h6>
              <div className="text-muted small">Totonoを使い始めるための設定を行いましょう</div>
            </div>
            <button
              className="btn btn-sm btn-link text-muted text-decoration-none"
              onClick={_ => setDismissed(true)}
            >
              閉じる
            </button>
          </div>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="small text-muted">{completedCount} / {steps.length} 完了</span>
            </div>
            <div className="progress" style={{ height: 6 }}>
              <div
                className="progress-bar bg-primary"
                style={{ width: `${(completedCount / steps.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            {steps.map(_ => {
              const isCompleted = completionStatus[_.key];
              const isCurrent = currentStep?.key === _.key;

              return (
                <div
                  key={_.key}
                  className={`d-flex align-items-start gap-3 p-3 rounded ${isCurrent ? 'bg-white shadow-sm' : ''}`}
                >
                  <div className="mt-1" style={{ flexShrink: 0 }}>
                    {isCompleted ? (
                      <span className="fas fa-check-circle text-success" />
                    ) : (
                      <span className={`far fa-circle ${isCurrent ? 'text-primary' : 'text-muted'}`} />
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className={`fw-bold small ${isCompleted ? 'text-muted text-decoration-line-through' : ''}`}>
                      {_.label}
                    </div>
                    {isCurrent && (
                      <>
                        <div className="text-muted small mt-1">{_.description}</div>
                        <div className="mt-2 d-flex gap-2 align-items-center">
                          {_.key === 'shopIntegration' ? (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => setShopModal('select')}
                            >
                              設定する
                            </button>
                          ) : (
                            <>
                              <Link to={_.path} className="btn btn-primary btn-sm">
                                設定する
                              </Link>
                              {_.guide && (
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => setGuideModal(_.guide)}
                                >
                                  手順を見る
                                </button>
                              )}
                            </>
                          )}
                          {_.skippable && (
                            <button
                              className="btn btn-link btn-sm text-muted text-decoration-none"
                              onClick={() => setSkippedSteps({ ...skippedSteps, [_.key]: true })}
                            >
                              {_.skipLabel}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <ShopIntegrationModal
        canUseShopify={canUseShopify}
        mode={shopModal}
        setMode={setShopModal}
        tenant={tenant}
      />

      <GuideModal
        guide={guideModal}
        onClose={_ => setGuideModal(null)}
      />
    </>
  );
}

function ShopIntegrationModal({ mode, setMode, tenant, canUseShopify }) {
  if (!mode) return null;

  const onClose = () => setMode(null);

  if (mode === 'select') {
    return (
      <Modal isOpen toggle={onClose}>
        <ModalHeader>ショップ連携</ModalHeader>
        <ModalBody>
          <div className="text-muted small mb-3">連携するショップの種類を選択してください。</div>
          <div className="d-flex flex-column gap-2">
            <button
              className="btn btn-outline-primary p-3 d-flex align-items-center gap-3 text-start"
              onClick={() => setMode('amazon')}
            >
              <span className="badge bg-warning text-dark px-2 py-1" style={{ fontSize: '0.85rem', flexShrink: 0 }}>Amazon</span>
              <div>
                <div className="fw-bold small">Amazonセラーを連携</div>
                <div className="text-muted small">SP API認証でAmazonアカウントと連携します</div>
              </div>
            </button>
            <button
              className="btn btn-outline-primary p-3 d-flex align-items-center gap-3 text-start"
              onClick={() => setMode('rakuten')}
            >
              <span className="badge bg-danger px-2 py-1" style={{ fontSize: '0.85rem', flexShrink: 0 }}>楽天</span>
              <div>
                <div className="fw-bold small">楽天ショップを連携</div>
                <div className="text-muted small">RMSのAPI情報を入力して連携します</div>
              </div>
            </button>
            {canUseShopify && (
            <button
              className="btn btn-outline-primary p-3 d-flex align-items-center gap-3 text-start"
              onClick={() => setMode('shopify')}
            >
              <span className="badge bg-success px-2 py-1" style={{ fontSize: '0.85rem', flexShrink: 0 }}>Shopify</span>
              <div>
                <div className="fw-bold small">Shopifyストアを連携</div>
                <div className="text-muted small">Shopifyアプリをインストールして連携します</div>
              </div>
            </button>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>閉じる</Button>
        </ModalFooter>
      </Modal>
    );
  }

  if (mode === 'amazon') {
    return (
      <Modal isOpen toggle={onClose} size="lg">
        <ModalHeader>Amazonセラーの連携</ModalHeader>
        <ModalBody>
          {amazonGuide.sections.map((section, i) => (
            <GuideSection key={i} section={section} index={i} />
          ))}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setMode('select')}>戻る</Button>
          <Button tag="a" href={env('SP_API_AUTH_CONSENT_URI')} color="primary">
            Amazonの認証ページへ
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  if (mode === 'rakuten') {
    return <RakutenShopModal tenant={tenant} onClose={onClose} />;
  }

  if (mode === 'shopify') {
    return <ShopifyShopModal tenant={tenant} onClose={onClose} />;
  }

  return null;
}

const rakutenShopFields = shopFields();
const rakutenInitialValues = { mall: 'rakuten' };
const renderRakutenFormHeader = () => (
  <>
    <RakutenFeeNote />
    <RakutenGuideAccordion />
  </>
);

function RakutenShopModal({ tenant, onClose }) {
  const onSubmit = async (values, { onClickClose }) => {
    const processed = processShopValues(values);
    try {
      await testRakutenApiConnection({
        rakutenServiceSecret: processed.rakutenServiceSecret,
        rakutenLicenseKey: processed.rakutenLicenseKey,
      });
    } catch (e) {
      console.error(e);
      toast.error('APIの接続テストに失敗しました。serviceSecretとライセンスキーを確認してください。');
      return;
    }
    try {
      const ref = doc(collection(tenant.ref, 'shops'));
      await setDoc(ref, { ...omit(processed, ['id', 'ref']), createdAt: new Date() });
      try {
        // 直近分は即時。過去分は「裏で商品取得 → 完了後に売上 backfill」。
        await createComputeSalesJob(tenant);
        await startRakutenSyncAndBackfill(tenant, ref.id);
      } catch (e) {
        // 取り込みに失敗しても定期実行でカバーされるため、追加完了は妨げない
        console.error(e);
      }
      toast.success(`楽天ショップを追加しました。売上データの取り込みを開始しました（過去${BACKFILL_DAYS}日分は順次反映されます）`);
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error('追加に失敗しました');
    }
  };

  return (
    <ModelFormModal
      isOpen
      title="楽天ショップの連携"
      fields={rakutenShopFields}
      values={rakutenInitialValues}
      onSubmit={onSubmit}
      onClickClose={onClose}
      renderFormHeader={renderRakutenFormHeader}
    />
  );
}

const shopifyShopFields = shopFields();
const shopifyInitialValues = { mall: 'shopify' };

// Shopify は「店舗を作る → OAuth でアプリをインストール」の2段階。
// 店舗登録の完了後、そのままインストール画面へ送り出す。
function ShopifyShopModal({ tenant, onClose }) {
  const onSubmit = async (values, { onClickClose }) => {
    const processed = processShopValues(values);
    try {
      const ref = doc(collection(tenant.ref, 'shops'));
      await setDoc(ref, { ...omit(processed, ['id', 'ref']), createdAt: new Date() });
      try {
        await createComputeSalesJob(tenant);
      } catch (e) {
        console.error(e);
      }
      toast.success('Shopifyストアを追加しました。続けてアプリをインストールしてください');
      onClickClose();
      // インストール用のOAuthを新規タブで開く。完了すると売上の取り込みが始まる
      window.open(`${env('CLOUD_FUNCTIONS_ENDPOINT')}/authShopify?tenantId=${tenant.id}&shopId=${ref.id}`, '_blank', 'noopener');
    } catch (e) {
      console.error(e);
      toast.error('追加に失敗しました');
    }
  };

  return (
    <ModelFormModal
      isOpen
      title="Shopifyストアの連携"
      fields={shopifyShopFields}
      values={shopifyInitialValues}
      onSubmit={onSubmit}
      onClickClose={onClose}
      renderFormHeader={() => (
        <div className="alert alert-info small">
          店舗名を入力して保存すると、Shopifyのアプリインストール画面が開きます。
          インストールを承認すると売上データの取り込みが始まります。
        </div>
      )}
    />
  );
}

function RakutenGuideAccordion() {
  const [showGuide, toggleGuide] = useToggle(false);

  return (
    <div className="mb-3">
      <button
        type="button"
        className="btn btn-link btn-sm text-decoration-none p-0"
        onClick={toggleGuide}
      >
        <span className={`fas fa-chevron-${showGuide ? 'up' : 'down'} me-1`} />
        入力項目の確認方法を見る
      </button>
      {showGuide && (
        <div className="mt-2 p-3 bg-light rounded">
          {rakutenGuide.sections.map((section, i) => (
            <GuideSection key={i} section={section} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}

function GuideSection({ section, index }) {
  return (
    <div className={index > 0 ? 'mt-4' : ''}>
      <h6 className="fw-bold mb-3">{section.heading}</h6>
      <ol className="mb-2">
        {section.steps.map((step, j) => (
          <li key={j} className="mb-2" style={{ fontSize: '0.9rem' }}>{step}</li>
        ))}
      </ol>
      {section.image && (
        <div className="mb-2">
          <img src={section.image} alt="" className="img-fluid border rounded" />
          {section.imageCaption && (
            <div className="text-muted small mt-1">{section.imageCaption}</div>
          )}
        </div>
      )}
      {section.note && (
        <div className="alert alert-info py-2 small mb-0">
          {section.note}
        </div>
      )}
      {section.referenceUrl && (
        <div className="small mt-2">
          <a href={section.referenceUrl} target="_blank" rel="noopener noreferrer">
            楽天公式の参考ページ <span className="fas fa-external-link-alt ms-1" />
          </a>
        </div>
      )}
    </div>
  );
}

function GuideModal({ guide, onClose }) {
  if (!guide) return null;

  return (
    <Modal isOpen toggle={onClose} size="lg">
      <ModalHeader>{guide.title}</ModalHeader>
      <ModalBody>
        {guide.sections.map((section, i) => (
          <GuideSection key={i} section={section} index={i} />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose}>閉じる</Button>
      </ModalFooter>
    </Modal>
  );
}
